import { Component, OnInit, inject } from "@angular/core";
import { Router } from "@angular/router";
import { ItemBrowserComponent } from "../../../../Common/Components/ItemBrowser/ItemBrowserComponent";
import {
  ItemBrowserConfig,
  ItemBrowserPagination,
  ItemBrowserSearchEvent,
  ItemBrowserDateRangeEvent,
  ItemBrowserCardAction,
} from "../../../../Common/Components/ItemBrowser/Type/ItemBrowserType";
import { MaterialListItem } from "../../Dto/MaterialModel";
import { MaterialEffects } from "../../State/MaterialEffects";
import { MaterialState } from "../../State/MaterialState";
import { ConfirmModalEffects } from "../../../../Common/Components/ConfirmModal/State/ConfirmModalEffects";
import { ToastEffects } from "../../../../Common/Components/Toast/State/ToastEffects";
import { formatCurrency } from "../../../../Common/Utility/UtilityNumber";
import { getBackendErrorMessage } from "../../../../Common/Utility/UtilityError";

@Component({
  selector: "app-material-home",
  standalone: true,
  imports: [ItemBrowserComponent],
  templateUrl: "./MaterialHomePage.html",
  styleUrl: "./MaterialHomePage.scss",
})
export class MaterialHomePage implements OnInit {
  private readonly materialEffects = inject(MaterialEffects);
  private readonly router = inject(Router);
  private readonly confirmModalEffects = inject(ConfirmModalEffects);
  private readonly toastEffects = inject(ToastEffects);
  readonly materialState = inject(MaterialState);

  get materialsConfig(): ItemBrowserConfig<MaterialListItem> {
    return {
      loading: this.materialState.loading(),
      emptyMessage: "No materials found",
      showSearch: true,
      searchPlaceholder: "Search materials...",
      showCreate: true,
      createButtonLabel: "Create Material",
      createButtonIcon: "add_shopping_cart",
      showDateRangeFilter: true,
      dateRangeFilterLabel: "Filter by creation date",
      imageOrientation: "portrait",
      onCreate: () => this.createMaterial(),
      cardTitleTemplate: (item) => item.name,
      cardSubtitleTemplate: (item) => item.sku,
      cardContentTemplate: (item) => `${formatCurrency(item.price)} | Series ID ${item.dcmSeriesId ?? "-"}`,
      actionButtons: [
        {
          icon: "edit",
          label: "Edit",
          handler: (item: MaterialListItem) => this.editMaterial(item),
          visible: () => true,
        },
        {
          icon: "delete",
          label: "Delete",
          handler: (item: MaterialListItem) => this.deleteMaterial(item),
          visible: () => true,
        },
      ],
    };
  }

  get materialsPagination(): ItemBrowserPagination {
    return this.materialState.pagination();
  }

  ngOnInit(): void {
    this.materialEffects.searchMaterials().catch(() => undefined);
  }

  onPageChange(event: { page: number; search?: string }): void {
    const currentSearchParams = this.materialState.searchParams();
    this.materialEffects.searchMaterials({ ...currentSearchParams, page: event.page, search: event.search }).catch(() => undefined);
  }

  onItemsPerPageChange(event: { limit: number; search?: string }): void {
    const currentSearchParams = this.materialState.searchParams();
    this.materialEffects
      .searchMaterials({ ...currentSearchParams, page: 1, limit: event.limit, search: event.search })
      .catch(() => undefined);
  }

  onActionClick<T>(event: { action: ItemBrowserCardAction<T>; item: T }): void {
    void event;
  }

  onSearchChange(event: ItemBrowserSearchEvent): void {
    this.materialEffects.searchMaterials({ search: event.term, page: event.page }).catch(() => undefined);
  }

  onDateRangeChange(event: ItemBrowserDateRangeEvent): void {
    const currentSearchParams = this.materialState.searchParams();
    this.materialEffects
      .searchMaterials({
        ...currentSearchParams,
        page: event.page,
        createdAtFrom: event.dateRange.from,
        createdAtTo: event.dateRange.to,
      })
      .catch(() => undefined);
  }

  createMaterial(): void {
    this.router.navigate(["/materials/create"]).catch(console.error);
  }

  editMaterial(material: MaterialListItem): void {
    this.router.navigate(["/materials/edit", material.id], { state: { material } }).catch(console.error);
  }

  deleteMaterial(material: MaterialListItem): void {
    this.confirmModalEffects.open({
      title: "Delete material?",
      message: `This will permanently delete ${material.name} (${material.sku}).`,
      confirmText: "Delete material",
      cancelText: "Cancel",
      variant: "danger",
      closeOnOverlay: true,
      onConfirm: async () => {
        try {
          await this.materialEffects.deleteMaterial(material.id);
          this.toastEffects.show("Material deleted", "success");
        } catch (exception: unknown) {
          this.toastEffects.show(getBackendErrorMessage(exception, "Failed to delete material"), "error");
        }
      },
    });
  }
}
